import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';

import { useCheckoutStore } from './store/checkout.store';
import CatalogPage from './pages/Catalog';
import ProductPage from './pages/Product';
import CartPage from './pages/Cart';
import CheckoutPage from './pages/Checkout';
import AboutPage from './pages/About';
import ProfilePage from './pages/Profile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ConsentScreen from './pages/ConsentScreen';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCatalog from './pages/admin/AdminCatalog';
import AdminUsers from './pages/admin/AdminUsers';
import AdminExport from './pages/admin/AdminExport';
import AdminForecast from './pages/admin/AdminForecast';
import AdminMarketing from './pages/admin/AdminMarketing';
import AdminPromocodes from './pages/admin/AdminPromocodes';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminAccessGate from './pages/admin/AdminAccessGate';
import { getMe } from './api/users.api';
import { useWebVitals } from './hooks/useWebVitals';
import { waitForInitData, getTelegramInitData } from './utils/initData';
import { useAdminConfig } from './hooks/useAdminConfig';
import { ADMIN_PERMISSIONS } from './config/admin-permissions';

import './styles/index.css';

type BootState =
  | { status: 'loading' }
  | { status: 'error'; message: string; kind: 'auth' | 'network' | 'unknown'; rawError?: unknown }
  | { status: 'ok'; consentAccepted: boolean };

/** При открытии приложения: если есть незавершённый заказ (оформление/оплата), открыть чекаут */
function InitialRedirect() {
  const pendingOrderId = useCheckoutStore((s) => s.pendingOrderId);
  if (pendingOrderId != null) {
    return <Navigate to="/checkout" replace />;
  }
  return <Navigate to="/catalog" replace />;
}

function AdminStartRedirect() {
  const { config, loading, error, reload } = useAdminConfig();
  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Загрузка…</div>;
  }

  if (!config) {
    return (
      <div className="admin-card admin-module-locked">
        <h2 className="admin-card-title">Не удалось загрузить конфигурацию админ-панели</h2>
        <p>{error || 'Сервер не вернул настройки доступа. Проверьте backend/.env (ADMIN_* переменные).'}</p>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={reload}>
          Повторить
        </button>
      </div>
    );
  }

  const hasCatalogAccess = config.access?.catalog === true;
  const hasMainAccess = config.access?.main === true;
  const permissions = new Set(config.permissions ?? []);

  if (permissions.has(ADMIN_PERMISSIONS.DASHBOARD_VIEW) && config?.modules?.dashboard) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.CATALOG_VIEW) || hasCatalogAccess) {
    return <Navigate to="/admin/catalog" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.USERS_VIEW) && config?.modules?.users) {
    return <Navigate to="/admin/users" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.EXPORT_VIEW) && config?.modules?.export) {
    return <Navigate to="/admin/export" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.FORECAST_VIEW) && config?.modules?.forecast) {
    return <Navigate to="/admin/forecast" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.PROMOCODES_VIEW) && config?.modules?.promocodes) {
    return <Navigate to="/admin/promocodes" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.AUDIT_VIEW)) {
    return <Navigate to="/admin/audit" replace />;
  }
  if (permissions.has(ADMIN_PERMISSIONS.MARKETING_VIEW) || hasMainAccess) {
    return <Navigate to="/admin/marketing" replace />;
  }

  return (
    <div className="admin-card admin-module-locked">
      <h2 className="admin-card-title">Нет доступных разделов</h2>
      <p>Обратитесь к владельцу бота, чтобы выдать доступ к нужным функциям админ-панели.</p>
    </div>
  );
}

function getBootErrorInfo(err: unknown): { message: string; kind: 'auth' | 'network' | 'unknown' } {
  const rawMessage =
    err instanceof Error && err.message.trim()
      ? err.message.trim()
      : 'Не удалось загрузить данные. Попробуйте ещё раз.';

  const m = rawMessage.toLowerCase();
  const kind: 'auth' | 'network' | 'unknown' =
    m.includes('авторизац') || m.includes('telegram') || m.includes('сессия') || m.includes('init data')
      ? 'auth'
      : m.includes('network') ||
          m.includes('timeout') ||
          m.includes('timed out') ||
          m.includes('подключ') ||
          m.includes('интернет') ||
          m.includes('соединени')
        ? 'network'
        : 'unknown';

  const message =
    kind === 'network'
      ? 'Не удалось подключиться к серверу. Проверьте интернет‑соединение и попробуйте ещё раз.'
      : rawMessage;

  return { message, kind };
}

function isMissingInitDataError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  return msg.includes('initdata') || msg.includes('x-telegram-init-data');
}

function buildErrorDetailsForCopy(rawError: unknown): string {
  const err = rawError as Error & { response?: { status?: number; config?: { url?: string }; data?: unknown } };
  const initDataNow = getTelegramInitData();
  const details: Record<string, unknown> = {
    message: err?.message ?? String(rawError),
    timestamp: new Date().toISOString(),
    initDataPresent: initDataNow ? true : false,
  };
  if (err?.response) {
    details.status = err.response.status;
    details.url = err.response.config?.url ?? err.response.config;
    const data = err.response.data as { error?: { message?: string; code?: string } } | undefined;
    if (data?.error) {
      details.backendCode = data.error.code;
      details.backendMessage = data.error.message;
    }
  }
  return JSON.stringify(details, null, 2);
}

function StartupErrorScreen({
  message,
  kind,
  rawError,
  onRetry,
}: {
  message: string;
  kind: 'auth' | 'network' | 'unknown';
  rawError?: unknown;
  onRetry: () => void;
}) {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const fullDetails = rawError ? buildErrorDetailsForCopy(rawError) : '';

  const handleCopy = () => {
    if (!fullDetails) return;
    navigator.clipboard.writeText(fullDetails).then(
      () => WebApp.showAlert?.('Скопировано в буфер обмена') ?? undefined,
      () => {}
    );
  };

  const handleRestart = () => {
    try {
      window.location.reload();
    } catch {
      // ignore
    }
  };

  const handleClose = () => {
    try {
      WebApp.close();
    } catch {
      // ignore
    }
  };

  const helpText =
    kind === 'auth'
      ? 'Похоже, Telegram не смог подтвердить сессию Mini App.'
      : kind === 'network'
        ? 'Похоже, есть проблема с подключением.'
        : 'Похоже, возникла временная ошибка.';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        padding: '24px',
        textAlign: 'center',
        color: 'var(--text-primary)',
      }}
    >
      <p style={{ fontSize: '16px', lineHeight: 1.5, marginBottom: '12px', maxWidth: '360px' }}>
        Не удалось загрузить данные
      </p>

      <p style={{ fontSize: '13px', color: 'var(--color-error)', margin: 0, maxWidth: '360px' }}>
        {message}
      </p>

      <p
        style={{
          fontSize: '12px',
          color: 'var(--text-secondary)',
          marginTop: '12px',
          marginBottom: '18px',
          maxWidth: '360px',
          lineHeight: 1.4,
        }}
      >
        {helpText}
        <br />
        <br />
        Что сделать:
        <br />
        1) Отключите VPN (если включён).
        <br />
        2) Нажмите «Перезапустить» или полностью закройте Mini App.
        <br />
        3) Откройте мини‑приложение снова из Telegram (меню бота или кнопка «Открыть»).
        <br />
        4) Очистите кэш браузера / Telegram (если обновление приложения не применилось).
      </p>

      {fullDetails && (
        <div style={{ width: '100%', maxWidth: '360px', marginBottom: '16px', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setDetailsExpanded(!detailsExpanded)}
              style={{
                padding: '6px 0',
                border: 'none',
                background: 'none',
                color: 'var(--color-accent)',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {detailsExpanded ? '▼ Скрыть подробности' : '▶ Показать подробности'}
            </button>
            <button
              type="button"
              onClick={handleCopy}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid var(--border-soft)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              📋 Копировать
            </button>
          </div>
          {detailsExpanded && (
            <pre
              style={{
                margin: '8px 0 0 0',
                padding: '12px',
                fontSize: '11px',
                background: 'var(--bg-surface)',
                borderRadius: '8px',
                overflow: 'auto',
                maxHeight: '180px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {fullDetails}
            </pre>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '280px' }}>
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '14px 20px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: 'var(--color-accent)',
            color: 'var(--text-on-accent)',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          🔄 Повторить
        </button>

        <button
          type="button"
          onClick={handleRestart}
          style={{
            padding: '14px 20px',
            borderRadius: '12px',
            border: '1px solid var(--border-soft)',
            backgroundColor: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            fontSize: '15px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          ♻️ Перезапустить
        </button>

        <button
          type="button"
          onClick={handleClose}
          style={{
            padding: '14px 20px',
            borderRadius: '12px',
            border: '1px solid var(--border-soft)',
            backgroundColor: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            fontSize: '15px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}

function App() {
  useWebVitals();
  const [isReady, setIsReady] = useState(false);
  const [boot, setBoot] = useState<BootState>({ status: 'loading' });

  useEffect(() => {
    try {
      WebApp.ready();
      WebApp.expand();
      try { WebApp.disableClosingConfirmation?.(); } catch { /* ignore */ }
      // Отключаем закрытие Mini App свайпом вниз — иначе при скролле внизу каталога
      // overscroll ошибочно сворачивает приложение. Закрыть можно только через хедер.
      try {
        (window as { Telegram?: { WebApp?: { disableVerticalSwipes?: () => void } } }).Telegram?.WebApp?.disableVerticalSwipes?.();
      } catch { /* ignore */ }

      const getThemeColor = (varName: string, fallback: `#${string}`): `#${string}` => {
        const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
        if (value && value.startsWith('#')) return value as `#${string}`;
        return fallback;
      };
      const bgColor = getThemeColor('--bg-main', '#FFFFFF');
      WebApp.setHeaderColor(bgColor);
      WebApp.setBackgroundColor(bgColor);

      WebApp.onEvent('viewportChanged', () => {});

      if (WebApp.BackButton) {
        WebApp.BackButton.onClick(() => window.history.back());
      }

      setIsReady(true);
    } catch (error) {
      console.error('Error initializing Telegram WebApp:', error);
      setIsReady(true);
    }
  }, []);

  const loadMe = useCallback(() => {
    setBoot({ status: 'loading' });
    return getMe()
      .then((data) => {
        setBoot({ status: 'ok', consentAccepted: !!data.privacyPolicyAcceptedAt });
      })
      .catch((err) => {
        const info = getBootErrorInfo(err);
        setBoot({ status: 'error', message: info.message, kind: info.kind, rawError: err });
      });
  }, []);

  useEffect(() => {
    if (!isReady) return;
    let cancelled = false;
    setBoot({ status: 'loading' });
    const requireInitData = async (maxWaitMs: number) => {
      await waitForInitData(maxWaitMs, 80);
      if (!getTelegramInitData()) {
        throw new Error('Telegram не передал initData (X-Telegram-Init-Data отсутствует).');
      }
    };
    void requireInitData(4500)
      .then(() => getMe())
      .then((data) => {
        if (!cancelled) setBoot({ status: 'ok', consentAccepted: !!data.privacyPolicyAcceptedAt });
      })
      .catch((err) => {
        if (cancelled) return;
        const info = getBootErrorInfo(err);
        if (info.kind !== 'auth') {
          setBoot({ status: 'error', message: info.message, kind: info.kind, rawError: err });
          return;
        }
        // Один повтор при auth-ошибке: подождать 2 с, снова подождать initData, затем getMe
        setTimeout(() => {
          if (cancelled) return;
          void requireInitData(2500)
            .then(() => getMe())
            .then((data) => {
              if (!cancelled) setBoot({ status: 'ok', consentAccepted: !!data.privacyPolicyAcceptedAt });
            })
            .catch((retryErr) => {
              if (!cancelled) {
                const retryInfo = getBootErrorInfo(retryErr);
                if (retryInfo.kind === 'auth') {
                  // If initData is fully absent (opened outside Telegram), do not fail-open:
                  // show actionable error instead of endless 401 loop.
                  if (isMissingInitDataError(retryErr)) {
                    setBoot({
                      status: 'error',
                      message: 'Приложение открыто вне Telegram Mini App (нет initData). Откройте его из меню бота в Telegram.',
                      kind: 'auth',
                      rawError: retryErr,
                    });
                  } else {
                    // For transient Telegram auth glitches keep fail-open behavior.
                    setBoot({ status: 'ok', consentAccepted: true });
                  }
                } else {
                  setBoot({ status: 'error', message: retryInfo.message, kind: retryInfo.kind, rawError: retryErr });
                }
              }
            });
        }, 2000);
      });
    return () => {
      cancelled = true;
    };
  }, [isReady]);

  if (!isReady || boot.status === 'loading') {
    return <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Загрузка…</div>;
  }

  if (boot.status === 'error') {
    return (
      <StartupErrorScreen
        message={boot.message}
        kind={boot.kind}
        rawError={boot.rawError}
        onRetry={loadMe}
      />
    );
  }

  if (!boot.consentAccepted) {
    return (
      <BrowserRouter basename="/">
        <Routes>
          <Route path="/rules" element={<PrivacyPolicy />} />
          <Route
            path="*"
            element={
              <ConsentScreen
                onConsentAccepted={() => setBoot({ status: 'ok', consentAccepted: true })}
              />
            }
          />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter basename="/">
      <Routes>
        <Route path="/" element={<InitialRedirect />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/rules" element={<PrivacyPolicy />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminStartRedirect />} />
          <Route path="dashboard" element={<AdminAccessGate moduleKey="dashboard" requirePermission={ADMIN_PERMISSIONS.DASHBOARD_VIEW}><AdminDashboard /></AdminAccessGate>} />
          <Route path="catalog" element={<AdminAccessGate requirePermission={ADMIN_PERMISSIONS.CATALOG_VIEW}><AdminCatalog /></AdminAccessGate>} />
          <Route path="users" element={<AdminAccessGate moduleKey="users" requirePermission={ADMIN_PERMISSIONS.USERS_VIEW}><AdminUsers /></AdminAccessGate>} />
          <Route path="marketing" element={<AdminAccessGate moduleKey="marketing" requirePermission={ADMIN_PERMISSIONS.MARKETING_VIEW}><AdminMarketing /></AdminAccessGate>} />
          <Route path="promocodes" element={<AdminAccessGate moduleKey="promocodes" requirePermission={ADMIN_PERMISSIONS.PROMOCODES_VIEW}><AdminPromocodes /></AdminAccessGate>} />
          <Route path="audit" element={<AdminAccessGate requirePermission={ADMIN_PERMISSIONS.AUDIT_VIEW}><AdminAuditLog /></AdminAccessGate>} />
          <Route path="forecast" element={<AdminAccessGate moduleKey="forecast" requirePermission={ADMIN_PERMISSIONS.FORECAST_VIEW}><AdminForecast /></AdminAccessGate>} />
          <Route path="export" element={<AdminAccessGate moduleKey="export" requirePermission={ADMIN_PERMISSIONS.EXPORT_VIEW}><AdminExport /></AdminAccessGate>} />
        </Route>
        <Route path="*" element={<Navigate to="/catalog" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
